import {
  getStripeLoginUrl,
  removeStripe,
  verifyStripeMode,
  getStripeConnectUrl,
} from '@client/new/api/stripe.api';
import { PrimaryButton } from '@client/new/components/button';
import { useUser } from '@client/new/hooks/use-user';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { StripePaymentMethodAlert } from './StripePaymentMethodAlert';
import { Typography } from '@mui/material';

const StripeConnect = ({ influencerId }: { influencerId: number }) => {
  const [fetchLink, setFetchLink] = useState(false);

  useEffect(() => {
    const getConnectUrl = () => {
      getStripeConnectUrl(influencerId)
        .then((link) => {
          window.location.href = link;
        })
        .catch((error) => {
          toast.error(error.message);
          setFetchLink(false);
        });
    };

    fetchLink && getConnectUrl();
  }, [fetchLink]);

  return (
    <div className="flex flex-col gap-4">
      <PrimaryButton onClick={() => setFetchLink(true)} disabled={fetchLink}>
        Connect Stripe
      </PrimaryButton>
      {fetchLink && <Typography>Generating stripe link ...</Typography>}
    </div>
  );
};

const VerifyStripeConnect = ({ influencerId }: { influencerId: number }) => {
  const [verify, setVerify] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const { refetchInfluencer } = useUser();

  useEffect(() => {
    const verifyStripe = () => {
      verifyStripeMode(influencerId)
        .then((isLive) => {
          if (!isLive) {
            setStatus(
              'Stripe account is missing some data. Try connecting again',
            );
          } else {
            toast.success('Stripe verified successfully');
            refetchInfluencer();
          }
          setVerify(false);
        })
        .catch((error) => {
          toast.error(error.message);
          setVerify(false);
        });
    };

    verify && verifyStripe();
  }, [verify]);

  return (
    <div className="flex flex-col gap-4">
      <PrimaryButton
        onClick={() => setVerify(true)}
        disabled={verify}
        className="w-[125px]"
      >
        Verify Stripe
      </PrimaryButton>
      {status && <Typography>{status}</Typography>}
      {verify && <Typography>Verifying account...</Typography>}
    </div>
  );
};

const StripeConnectDashboard = () => {
  const { influencer } = useUser();
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (influencer) {
      getStripeLoginUrl(influencer.id).then((res) => {
        setUrl(res);
      });
    }
  }, [influencer]);

  return (
    <a
      className="bg-primary rounded-full px-10 py-4 text-white font-bold"
      href={url}
    >
      Go to dashboard
    </a>
  );
};

const RemoveStripe = () => {
  const { influencer, refetchInfluencer } = useUser();

  const remove = useCallback(() => {
    if (!influencer?.id) return;
    removeStripe(influencer.id)
      .then(() => toast.success('Stripe removed'))
      .then(() => refetchInfluencer())
      .catch(() => toast.error('Error removing stripe'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [influencer]);

  return (
    <button
      className="bg-gray-200 rounded-full px-10 py-4 text-zinc-900 font-bold"
      onClick={remove}
    >
      Remove Stripe
    </button>
  );
};

export const StripeConnectSettings = () => {
  const { influencer } = useUser();

  if (!influencer) {
    return <p>Stripe Connect is only available for creators.</p>;
  }

  if (influencer?.stripeUserId && influencer?.stripeLiveMode) {
    return (
      <div className="flex flex-col gap-2">
        <StripePaymentMethodAlert />
        <p>Stripe connected</p>
        <div className="flex gap-2">
          <StripeConnectDashboard />
          <RemoveStripe />
        </div>
      </div>
    );
  }

  const showVerifyStripe =
    influencer?.stripeUserId && !influencer?.stripeLiveMode;
  return (
    <div className="flex flex-col gap-2 items-start">
      <p>Stripe not connected</p>
      <div className="flex gap-2">
        <StripeConnect influencerId={influencer.id} />
      </div>
      {showVerifyStripe && (
        <div className="flex gap-2">
          <VerifyStripeConnect influencerId={influencer.id} />
        </div>
      )}
    </div>
  );
};
